/**
 * DocumentationBrowser Component - Modal for browsing and linking project documents to an issue
 * Uses document-service APIs (document_relations) instead of work-service documentation system.
 */

import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { documentsApi, type EditorDocument } from '../../../api/documents';
import { FileText, BookOpen, StickyNote, Layout, Search, X } from 'lucide-react';

interface DocumentationBrowserProps {
  projectId: string;
  issueId: string;
  existingDocIds: string[];
  onClose: () => void;
  onLinked: () => void;
}

const DOC_TYPE_LABELS: Record<string, { label: string; Icon: typeof FileText }> = {
  spec: { label: 'Spec', Icon: BookOpen },
  guide: { label: 'Guide', Icon: Layout },
  note: { label: 'Note', Icon: StickyNote },
  document: { label: 'Document', Icon: FileText },
  template: { label: 'Template', Icon: FileText },
  test: { label: 'Test', Icon: FileText },
};

const DOC_TYPE_OPTIONS = ['all', 'document', 'spec', 'guide', 'note', 'template', 'test'] as const;

export function DocumentationBrowser({
  projectId,
  issueId,
  existingDocIds,
  onClose,
  onLinked,
}: DocumentationBrowserProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState<string>('all');

  // Fetch all project documents
  const { data, isLoading } = useQuery({
    queryKey: ['project-docs', projectId],
    queryFn: () => documentsApi.list(projectId),
    enabled: !!projectId,
  });

  const allDocs = data?.documents ?? [];

  // Filter documents
  const filteredDocs = allDocs.filter((doc) => {
    if (selectedType !== 'all' && doc.doc_type !== selectedType) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      if (
        !doc.title.toLowerCase().includes(q) &&
        !(doc.tags || []).some((t: string) => t.toLowerCase().includes(q))
      ) {
        return false;
      }
    }
    return true;
  });

  // Link mutation
  const linkMutation = useMutation({
    mutationFn: async (docId: string) => {
      await documentsApi.links.create(docId, projectId, {
        target_type: 'issue',
        target_entity_id: issueId,
        relation_type: 'linked',
      });
    },
    onSuccess: () => {
      onLinked();
    },
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex justify-between items-center mb-3">
            <h2 className="text-lg font-semibold text-gray-900">Link Document</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
              disabled={linkMutation.isPending}
            >
              <X size={20} />
            </button>
          </div>

          {/* Search and filters */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search documents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent text-sm"
            >
              {DOC_TYPE_OPTIONS.map((t) => (
                <option key={t} value={t}>
                  {t === 'all' ? 'All Types' : (DOC_TYPE_LABELS[t]?.label ?? t)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="text-center py-8 text-gray-500">
              <p className="text-sm">Loading documents...</p>
            </div>
          ) : filteredDocs.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-3 text-gray-400" />
              <p className="text-sm font-medium">No documents found</p>
              <p className="text-xs text-gray-400 mt-1">
                {searchQuery ? 'Try adjusting your search' : 'No documents in this project yet'}
              </p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {filteredDocs.map((doc) => {
                const isLinked = existingDocIds.includes(doc.id);
                const typeInfo = DOC_TYPE_LABELS[doc.doc_type] || DOC_TYPE_LABELS.document;

                return (
                  <div
                    key={doc.id}
                    className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <typeInfo.Icon size={16} className="text-gray-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">{doc.title}</div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-gray-500">{typeInfo.label}</span>
                        {(doc.tags || []).length > 0 && (
                          <span className="text-xs text-gray-400">
                            {(doc.tags as string[]).slice(0, 2).join(', ')}
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => linkMutation.mutate(doc.id)}
                      disabled={linkMutation.isPending || isLinked}
                      className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                    >
                      {linkMutation.isPending && linkMutation.variables === doc.id
                        ? 'Linking...'
                        : isLinked
                          ? 'Linked'
                          : 'Link'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
            disabled={linkMutation.isPending}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
